import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CheckCircle2,
  MessageSquare,
  AlertCircle,
  FileImage,
  Film,
  FileText,
  File,
  Lightbulb,
} from 'lucide-react';
import { cn, formatTimeAgo } from '../../lib/utils';

export interface ActivityItem {
  id: string;
  type: 'work_item' | 'comment' | 'request_comment' | 'request';
  action: string;
  workItemId: string | null;
  workItemTitle: string | null;
  workItemThumbnail: string | null;
  workItemType: 'image' | 'video' | 'document' | 'other' | null;
  projectId: string;
  projectName: string;
  actorId: string;
  actorName: string;
  actorAvatar: string | null;
  actorIsAdmin: boolean;
  commentPreview: string | null;
  requestId: string | null;
  requestTitle: string | null;
  linkUrl: string | null;
  createdAt: string;
}

const statusConfig: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  // Work item statuses
  approved: {
    label: 'Approved',
    color: 'text-green-500 bg-green-500/10',
    icon: <CheckCircle2 className="w-4 h-4" />,
  },
  approval_requested: {
    label: 'Awaiting Approval',
    color: 'text-amber-500 bg-amber-500/10',
    icon: <AlertCircle className="w-4 h-4" />,
  },
  changes_requested: {
    label: 'Changes Requested',
    color: 'text-red-500 bg-red-500/10',
    icon: <AlertCircle className="w-4 h-4" />,
  },
  seeking_commentary: {
    label: 'Feedback Requested',
    color: 'text-blue-500 bg-blue-500/10',
    icon: <MessageSquare className="w-4 h-4" />,
  },
  comment_added: {
    label: 'New Comment',
    color: 'text-accent bg-accent/10',
    icon: <MessageSquare className="w-4 h-4" />,
  },
  // Request statuses
  new: {
    label: 'New Request',
    color: 'text-blue-500 bg-blue-500/10',
    icon: <Lightbulb className="w-4 h-4" />,
  },
  under_review: {
    label: 'Under Review',
    color: 'text-amber-500 bg-amber-500/10',
    icon: <AlertCircle className="w-4 h-4" />,
  },
  plan_proposed: {
    label: 'Plan Proposed',
    color: 'text-purple-500 bg-purple-500/10',
    icon: <FileText className="w-4 h-4" />,
  },
  converted: {
    label: 'Converted',
    color: 'text-cyan-500 bg-cyan-500/10',
    icon: <CheckCircle2 className="w-4 h-4" />,
  },
  declined: {
    label: 'Declined',
    color: 'text-red-500 bg-red-500/10',
    icon: <AlertCircle className="w-4 h-4" />,
  },
};

const typeIcons: Record<string, React.ReactNode> = {
  image: <FileImage className="w-5 h-5" />,
  video: <Film className="w-5 h-5" />,
  document: <FileText className="w-5 h-5" />,
  other: <File className="w-5 h-5" />,
};

function getActionText(item: ActivityItem): string {
  if (item.type === 'comment') {
    return 'commented on';
  }
  if (item.type === 'request_comment') {
    return 'commented on request';
  }
  if (item.type === 'request') {
    switch (item.action) {
      case 'new':
        return 'submitted request';
      case 'under_review':
        return 'is reviewing request';
      case 'plan_proposed':
        return 'proposed a plan for';
      case 'approved':
        return 'approved request';
      case 'converted':
        return 'converted request';
      case 'declined':
        return 'declined request';
      default:
        return 'updated request';
    }
  }
  // Work item actions
  switch (item.action) {
    case 'approved':
      return 'approved';
    case 'approval_requested':
      return 'requested approval for';
    case 'changes_requested':
      return 'requested changes on';
    case 'seeking_commentary':
      return 'requested feedback on';
    default:
      return 'updated';
  }
}

function getItemTitle(item: ActivityItem): string {
  if (item.type === 'request_comment' || item.type === 'request') {
    return item.requestTitle || 'Untitled Request';
  }
  return item.workItemTitle || 'Untitled';
}

function getItemLink(item: ActivityItem): string {
  if ((item.type === 'request_comment' || item.type === 'request') && item.requestId) {
    return `/requests/${item.requestId}`;
  }
  return `/work-items/${item.workItemId}`;
}

interface ActivityCardProps {
  item: ActivityItem;
  index?: number;
}

export function ActivityCard({ item, index = 0 }: ActivityCardProps) {
  const config = statusConfig[item.action] || statusConfig.seeking_commentary;
  const isRequestComment = item.type === 'request_comment';
  const isRequest = item.type === 'request';
  const isRequestRelated = isRequestComment || isRequest;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <Link to={getItemLink(item)}>
        <motion.div
          whileHover={{ y: -4, scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
          className="bg-white/80 dark:bg-[#1a1a1f]/80 backdrop-blur-xl rounded-2xl border border-border/50 p-4 transition-shadow hover:shadow-lg hover:shadow-accent/5 shadow-sm"
        >
          {/* Header: Actor + Time */}
          <div className="flex items-center gap-3 mb-3">
            {item.actorAvatar ? (
              <img
                src={item.actorAvatar}
                alt={item.actorName}
                className="w-10 h-10 rounded-full object-cover ring-2 ring-border"
              />
            ) : (
              <div
                className={cn(
                  'w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold',
                  item.actorIsAdmin
                    ? 'bg-gradient-to-br from-accent to-accent-light'
                    : 'bg-gradient-to-br from-green-500 to-emerald-500'
                )}
              >
                {item.actorName.charAt(0)}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium truncate">{item.actorName}</span>
                {item.actorIsAdmin && (
                  <span className="text-xs px-1.5 py-0.5 rounded bg-accent/10 text-accent">
                    Team
                  </span>
                )}
              </div>
              <p className="text-sm text-text-muted">
                {getActionText(item)}{' '}
                <span className="text-text-secondary font-medium">
                  {getItemTitle(item)}
                </span>
              </p>
            </div>
            <span className="text-xs text-text-muted whitespace-nowrap">
              {formatTimeAgo(item.createdAt)}
            </span>
          </div>

          {/* Content: Thumbnail + Details */}
          <div className="flex gap-4">
            {/* Thumbnail */}
            <div className="flex-shrink-0">
              {isRequestRelated ? (
                <div className="w-20 h-20 rounded-xl bg-amber-500/10 flex items-center justify-center text-amber-500">
                  <Lightbulb className="w-8 h-8" />
                </div>
              ) : item.workItemThumbnail ? (
                <img
                  src={item.workItemThumbnail}
                  alt=""
                  className="w-20 h-20 rounded-xl object-cover bg-bg-tertiary"
                />
              ) : (
                <div className="w-20 h-20 rounded-xl bg-bg-tertiary flex items-center justify-center text-text-muted">
                  {typeIcons[item.workItemType || 'other'] || typeIcons.other}
                </div>
              )}
            </div>

            {/* Details */}
            <div className="flex-1 min-w-0">
              <p className="text-sm text-text-muted mb-2">{item.projectName}</p>

              {/* Status badge */}
              <div
                className={cn(
                  'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-sm font-medium',
                  isRequestComment ? 'text-amber-500 bg-amber-500/10' : config.color
                )}
              >
                {isRequestComment ? <MessageSquare className="w-4 h-4" /> : config.icon}
                {isRequestComment ? 'Request Comment' : isRequest ? config.label : config.label}
              </div>

              {/* Comment preview */}
              {item.commentPreview && (
                <p className="text-sm text-text-secondary mt-2 line-clamp-2">
                  "{item.commentPreview}"
                </p>
              )}
            </div>
          </div>
        </motion.div>
      </Link>
    </motion.div>
  );
}
